// last modified by AI tools on 2026-09-30
/**
 * Basic market information
 */
export interface ForecastTraderMarket {
  /** Market name */
  name: string;
  /** Market symbol */
  symbol: string;
  /** Market exchange */
  exchange: string;
  /** Market contract identifier */
  conid: number;
}

/**
 * Detailed market information with strikes and additional metadata
 */
export interface ForecastTraderDetailedMarket extends ForecastTraderMarket {
  /** Number of strikes in market */
  strike_count: number;
  /** List of strikes */
  strikes: ForecastTraderStrike[];
  /** Logo category to use in logo service to get image */
  logo_category: string;
  /** Navigation category identifier (from page nav, used for icon retrieval) */
  category?: string;
  /** All exchanges this product is available on */
  exchanges?: string[];
  /** Product conids for each exchange (for multi-venue OI aggregation) */
  productConids?: number[];
  /** "conid@exchange" pairs for all product conids (for agg-open-interest) */
  productConidExPairs?: string[];
  /** Aggregated open interest returned from the prediction product endpoint */
  open_interest?: number;
  /** Product conid when it differs from the underlying conid */
  product_conid?: number;
  /** True when this market is only visible because of the x-internal header */
  restricted?: boolean;
  /** True when this market is a FEX conditional contract (flagged by RefDB) */
  isConditional?: boolean;
}

/**
 * Forecast trader category information
 */
export interface ForecastTraderCategory {
  /** Category name */
  name: string;
  /** Optional identifier of parent category */
  parent_id?: string;
  /** Optional list of markets */
  markets?: ForecastTraderMarket[];
  /** True when this category is only visible because of the x-internal header */
  restricted?: boolean;
}

/**
 * Forecast trader category with ID for display purposes
 */
export interface ForecastTraderCategoryWithId extends ForecastTraderCategory {
  /** Category ID */
  id: string;
}

/**
 * Response for GET /tws.proxy/forecasttrader/category/tree
 */
export interface ForecastTraderCategoryTreeResponse {
  /** Categories indexed by category ID */
  categories: Record<string, ForecastTraderCategory>;
  /** Timestamp of data retrieval from refdb */
  as_of: string;
}

/**
 * Strike information for popular markets
 */
export interface ForecastTraderStrike {
  /** Strike label to display */
  strike_label: string;
  /** Can be year, year and months or year, month and date */
  measured_period: string;
  /** Last trade time in epoch seconds */
  last_trade_time: number;
  /** Contract identifier of "yes" representative contract */
  conid_yes: number;
  /** Contract identifier of "no" representative contract */
  conid_no: number;
  /** "conid@exchange" pairs for all yes contracts across exchanges (for flexbest bid-ask) */
  yesConidExPairs?: string[];
  /** "conid@exchange" pairs for all no contracts across exchanges (for flexbest bid-ask) */
  noConidExPairs?: string[];
  /** Political party affiliation (e.g. "Democrat", "Republican") */
  party?: string;
  /** True when this strike is only visible because of the x-internal header */
  restricted?: boolean;
  /** Exchange of the matched representative contract (for correct bid-ask/OI lookups) */
  exchange?: string;
  /** Seed ask price for YES side (0-1 range) */
  ask_yes?: number;
  /** Seed ask price for NO side (0-1 range) */
  ask_no?: number;
  /** Seed bid price for YES side (0-1 range) */
  bid_yes?: number;
  /** Seed bid price for NO side (0-1 range) */
  bid_no?: number;
  /** Seed open interest from product response */
  open_interest?: number;
  /** True when this contract is the center (representative) strike for the product */
  is_center?: boolean;
}

/**
 * Market information for category markets request
 */
export interface ForecastTraderCategoryMarketRequest {
  /** Market contract identifier */
  conid: number;
  /** Optional market exchange */
  exchange?: string;
}

/**
 * Request body for POST /tws.proxy/forecasttrader/market/category
 */
export interface ForecastTraderCategoryMarketsRequest {
  /** List of market conids */
  markets: ForecastTraderCategoryMarketRequest[];
}

/**
 * Response for POST /tws.proxy/forecasttrader/market/category
 */
export interface ForecastTraderCategoryMarketsResponse {
  /** List of markets */
  markets: ForecastTraderDetailedMarket[];
}

/**
 * Query parameters for GET /tws.proxy/forecasttrader/contract/representative
 */
export interface ForecastTraderRepresentativeContractParams {
  /** Underlying event index contract identifier */
  underlyingConid: number;
  /** Optional exchange of representative contracts */
  exchange?: string;
}

/**
 * Response for GET /tws.proxy/forecasttrader/contract/representative
 */
export interface ForecastTraderRepresentativeContractResponse {
  /** Contract identifier with optional exchange (format is conid@exchange) */
  conid_ex: string;
}

/**
 * Query parameters for GET /tws.proxy/forecasttrader/contract/market
 */
export interface ForecastTraderMarketContractsParams {
  /** Underlying event index contract identifier (existing usage) */
  underlyingConid?: number;
  /** Product contract identifier (new election usage) */
  productConid?: number;
  /** Optional exchange to filter contracts by */
  exchange?: string;
}

/**
 * Individual contract information for market contracts
 */
export interface ForecastTraderContract {
  /** Market contract identifier */
  conid: number;
  /** "Y" or "N", yes or no contract */
  side: string;
  /** Contract expiration date in YYYYMMDD format */
  expiration: string;
  /** Contract strike */
  strike: number;
  /** Strike label to display */
  strike_label: string;
  /** Label UI to show for the expiration tabs */
  expiry_label: string;
  /** Whether this candidate is an incumbent (optional - will be provided when available) */
  incumbent?: boolean;
  /** Party affiliation of the candidate (optional - will be provided when available) */
  party?: string;
}

/**
 * Response for GET /tws.proxy/forecasttrader/contract/market
 */
export interface ForecastTraderMarketContractsResponse {
  /** Name of contract's market */
  market_name: string;
  /** Exchange that was passed in request or determined internally */
  exchange: string;
  /** Market symbol */
  symbol: string;
  /** Logo category to use in logo service to get image */
  logo_category: string;
  /** Indicates whether UI should not show underlying chart */
  exclude_historical_data: boolean;
  /** List of contracts */
  contracts: ForecastTraderContract[];
  /** Timestamp of data retrieval from refdb */
  as_of: string;
}

/**
 * Query parameters for GET /tws.proxy/forecasttrader/contract/search
 */
export interface ForecastTraderContractSearchParams {
  /** Prefix entered by user */
  prefix: string;
}

/**
 * Search result contract information
 */
export interface ForecastTraderSearchContract {
  /** Market contract identifier */
  conid: number;
  /** "Y" or "N", yes or no contract */
  side: 'Y' | 'N';
  /** Name of contract's market */
  market_name: string;
  /** Strike label to display */
  strike_label: string;
  /** Can be year, year and months or year, month and date */
  measured_period: string;
  /** exchange */
  exchange: string;
  conid_yes: number;
  conid_no: number;
  product_conid: number;
  /** Political party affiliation (e.g. "Democrat", "Republican") */
  party?: string;
}

/**
 * Response for GET /tws.proxy/forecasttrader/contract/search
 */
export interface ForecastTraderContractSearchResponse {
  /** List of contracts matching the prefix */
  contracts: ForecastTraderSearchContract[];
}

/**
 * Query parameters for GET /tws.proxy/forecasttrader/contract/related
 */
export interface ForecastTraderRelatedContractParams {
  /** Contract identifier */
  conid: number;
  /** Optional, when provided service will perform additional restriction checks for specified account */
  restrictionAccount?: string;
}

/**
 * Response for GET /tws.proxy/forecasttrader/contract/related
 */
export interface ForecastTraderRelatedContractResponse {
  /** Contract question (i.e. "Will this happen on this date?") */
  question: string;
  /** Contract identifier of "yes" representative contract */
  conid_yes: number;
  /** Contract identifier of "no" representative contract */
  conid_no: number;
  /** Optional, set to true when related contract is not eligible for account provided in restrictionAccount query parameter */
  restricted?: boolean;
}

/**
 * Query parameters for GET /tws.proxy/forecasttrader/contract/rules
 */
export interface ForecastTraderContractRulesParams {
  /** Contract identifier */
  conid: number;
}

/**
 * Response for GET /tws.proxy/forecasttrader/contract/rules
 */
export interface ForecastTraderContractRulesResponse {
  /** Product asset class */
  asset_class: string;
  /** Long product description */
  description: string;
  /** Name of contract's market */
  market_name: string;
  /** Measured period */
  measured_period: string;
  /** Either strike or strike label depending on contract type */
  threshold: string;
  /** Name of source agency */
  source_agency: string;
  /** Link to data from source agency */
  data_url_resolution_link: string;
  /** Last trade time in epoch seconds */
  last_trade_time: number;
  /** Product code (symbol) */
  product_code: string;
  /** Link to market rules document */
  market_rules_link: string;
  /** Release time in epoch seconds */
  release_time: number;
  /** Payout time in epoch seconds */
  payout_time: number;
  /** Formatted payout amount */
  payout: string;
  /** Formatted price increment amount */
  price_increment: string;
  /** Tiered price incremenets based on price ranges */
  price_increments?: { lower_edge: string; increment: string }[];
  /** Exchange timezone */
  exchange_timezone: string;
}

/**
 * Query parameters for GET /tws.proxy/forecasttrader/contract/schedules
 */
export interface ForecastTraderContractSchedulesParams {
  /** Contract identifier */
  conid: number;
}

/**
 * Trading time interval
 */
export interface ForecastTraderTradingTime {
  /** Start of trading interval */
  open: string;
  /** End of trading interval */
  close: string;
}

/**
 * Trading schedule for a day
 */
export interface ForecastTraderTradingSchedule {
  /** Day of week */
  day_of_week: string;
  /** List of trading time intervals */
  trading_times: ForecastTraderTradingTime[];
}

/**
 * Response for GET /tws.proxy/forecasttrader/contract/schedules
 */
export interface ForecastTraderContractSchedulesResponse {
  /** Exchange timezone */
  timezone: string;
  /** List of trading schedules */
  trading_schedules: ForecastTraderTradingSchedule[];
}

/**
 * Query parameters for GET /tws.proxy/forecasttrader/contract/details
 */
export interface ForecastTraderContractDetailsParams {
  /** Contract identifier */
  conid: number;
}

/**
 * Response for GET /tws.proxy/forecasttrader/contract/details
 */
export interface ForecastTraderContractDetailsResponse {
  /** Contract identifier of "yes" contract */
  conid_yes: number;
  /** Contract identifier of "no" contract */
  conid_no: number;
  /** Contract question (i.e. "Will this happen on this date?") */
  question: string;
  /** "Y" or "N", yes or no contract */
  side: string;
  /** Strike label to display */
  strike_label: string;
  /** Whether this candidate is an incumbent (optional - will be provided when available) */
  incumbent?: boolean;
  /** Party affiliation of the candidate (optional - will be provided when available) */
  party?: string;
  /** True when this contract is a FEX conditional contract (flagged by RefDB). */
  is_conditional?: boolean;
  /** On a Conditional contract: the conditioning ("IF…") component strike contract identifier. */
  conditioning_strike_conid?: number;
  /** On a Conditional contract: the settlement ("WILL…") component strike contract identifier. */
  settlement_strike_conid?: number;
  /**
   * Display string for the conditioning ("If FFDEC…") component, e.g. "If FFDEC settles above 50K".
   * Mapped via conditioning_strike_conid in trpars (REFDB-19749).
   */
  conditioning_display_name?: string;
  /**
   * Display string for the settlement ("Then CPIY…") component, e.g. "Then CPIY comes in above 3%".
   * Mapped via settlement_strike_conid in trpars (REFDB-19749).
   */
  settlement_display_name?: string;
}

/**
 * Query parameters for GET /tws.proxy/forecasttrader/contract/mass/details
 */
export interface ForecastTraderMassContractDetailsParams {
  /** Array of contract identifiers */
  conids: number[];
}

/**
 * Individual contract details for mass contract details response
 */
export interface ForecastTraderMassContractDetailsContract {
  /** Contract identifier of "yes" contract */
  conid_yes: number;
  /** Contract identifier of "no" contract */
  conid_no: number;
  /** Contract question (i.e. "Will this happen on this date?") */
  question: string;
  /** "Y" or "N", yes or no contract */
  side: string;
  /** Strike label to display */
  strike_label: string;
  /** Contract strike */
  strike: number;
  /** Contract exchange */
  exchange: string;
  /** Contract expiration date in YYYYMMDD format */
  expiration: string;
  /** Contract symbol */
  symbol: string;
  /** Contract category */
  category: string;
  /** Logo category to use in logo service to get image */
  logo_category: string;
  /** Can be year, year and months or year, month and date */
  measured_period: string;
  market_name: string;
  product_conid?: number;
}

/**
 * Response for GET /tws.proxy/forecasttrader/contract/mass/details
 */
export interface ForecastTraderMassContractDetailsResponse {
  /** Array of contract details */
  contracts: ForecastTraderMassContractDetailsContract[];
}

/**
 * Election office types
 */
export type ElectionOffice = 'governor' | 'house' | 'senate';

/**
 * Political party types
 */
export type PoliticalParty = 'Democrat' | 'Republican' | 'Nonpartisan';

/**
 * Query parameters for GET /tws.proxy/forecasttrader/election/map
 * Discriminated union - TypeScript enforces party requirement for primaries
 */
export type ForecastTraderElectionMapParams =
  | { office: ElectionOffice; raceType: 'general' }
  | { office: ElectionOffice; raceType: 'primary'; party: PoliticalParty };

/**
 * Type guard to check if params are for a primary election
 */
export function isPrimaryElection(
  params: ForecastTraderElectionMapParams,
): params is { office: ElectionOffice; raceType: 'primary'; party: PoliticalParty } {
  return params.raceType === 'primary';
}

/**
 * Product data in election map response
 */
export interface ForecastTraderElectionMapProduct {
  /** Contract identifier of event product */
  conid: number;
  /** User friendly name to display */
  name: string;
}

/**
 * State data in election map response
 */
export interface ForecastTraderElectionMapState {
  /** Full state name (e.g., "New York", "North Carolina") */
  name: string;
  /** Party of leading candidate */
  party?: 'Democrat' | 'Republican' | 'Independent';
  /** Color intensity: 0=toss-up, 1=lean, 2=likely, 3=solid */
  color?: number;
  /** List of products for this state */
  products: ForecastTraderElectionMapProduct[];
}

/**
 * Response for GET /tws.proxy/forecasttrader/election/map
 */
export interface ForecastTraderElectionMapResponse {
  /** Aggregated open interest for all products (by office) */
  open_interest?: number;
  /** List of states with party sentiment data */
  states: ForecastTraderElectionMapState[];
}

/**
 * Query parameters for GET /tws.proxy/forecasttrader/election/top-oi
 */
export interface ForecastTraderElectionTopOIParams {
  /** Event product contract identifier */
  productConid: number;
  /** Sorting by field - "open_interest" or "ask_price" */
  sortBy?: 'open_interest' | 'ask_price';
}

/**
 * Contract data in election contracts response
 */
export interface ForecastTraderElectionTopOIContract {
  /** Contract identifier of "yes" contract */
  conid_yes: number;
  /** Contract identifier of "no" contract */
  conid_no: number;
  /** Contract question (e.g., "Will this happen on this date?") */
  question: string;
  /** Strike label - used as the candidate display name */
  strike_label: string;
  /** Contract exchange */
  exchange: string;
  /** Whether this candidate is the incumbent */
  incumbent?: boolean;
  /** Candidate party (e.g., "Democrat", "Republican") */
  party?: string;
  /** Party affiliation of the candidate (e.g., "Democrat", "Republican") */
  party_affiliation?: string;
  /** Candidate status code from the exchange (e.g., "I", "C", "O") */
  candidate_status?: string;
  /** Matched contracts from different exchanges */
  matched?: ForecastTraderElectionGridMatchedContract[];
}

/**
 * Response for GET /tws.proxy/forecasttrader/election/contracts
 */
export interface ForecastTraderElectionTopOIResponse {
  /** Name of contract's market */
  market_name: string;
  /** Party of leading candidate (Democrat, Republican, Independent), null when no leader */
  party?: string;
  /** Color intensity: 0=toss-up, 1=lean, 2=likely, 3=solid */
  color?: number;
  /** Conid of leading Democrat candidate */
  leading_dem_conid?: number;
  /** Conid of leading Republican candidate */
  leading_rep_conid?: number;
  /** State name (level3 category name, e.g., "Texas", "New York") */
  state?: string;
  /** Market-level open interest (sum across all candidates) */
  open_interest?: number;
  /** Total strikes for the market across all exchanges */
  strike_count?: number;
  /** Exchanges the market trades on (e.g., ["FORECASTX", "KALSHI"]) */
  exchanges?: string[];
  /** List of contracts sorted by open interest */
  contracts: ForecastTraderElectionTopOIContract[];
}

// Election Grid API Types

/**
 * Race type for elections
 */
export type ElectionRaceType = 'general' | 'primary';

/**
 * Sort options for election grid
 */
export type ElectionSortBy = 'name' | 'oi' | 'dem' | 'rep';

/**
 * Price party filter
 */
export type ElectionPriceParty = 'Democrat' | 'Republican';

/**
 * Request body for POST /tws.proxy/forecasttrader/election/grid
 */
export interface ForecastTraderElectionGridParams {
  /** Office type to filter by (query parameter) */
  office: ElectionOffice;
  /** Type of race - general or primary (query parameter) */
  raceType: ElectionRaceType;
  /** Required if raceType is primary (query parameter) */
  party?: PoliticalParty;
  /** Filters (request body) */
  filters?: {
    /** Product-level filters */
    product?: {
      /** Filter by specific state name */
      state?: string;
      /** Product contract IDs from PNL service */
      productConids?: number[];
    };

    /** Contract-level filters */
    contract?: {
      /** Minimum contract price (1-99) */
      min_price?: number;
      /** Maximum contract price (1-99) */
      max_price?: number;
      /** Party to use for price filtration */
      party?: ElectionPriceParty;
    };
  };

  /** Sorting configuration (request body) */
  sorting?: {
    /** Sorting method for products */
    product?: 'name' | 'open_interest';
  };
}

/**
 * Matched contract from a different exchange
 */
export interface ForecastTraderElectionGridMatchedContract {
  /** "Yes" contract ID on the matched exchange */
  conid_yes: number;
  /** "No" contract ID on the matched exchange */
  conid_no: number;
  /** Exchange of the matched contract */
  exchange: string;
  /** Product (event/race) conid on the matched exchange */
  product_conid: number;
}

/**
 * Contract information in election grid product
 */
export interface ForecastTraderElectionGridContract {
  /** "Yes" contract ID for trading */
  conid_yes: number;
  /** "No" contract ID for trading */
  conid_no: number;
  /** Display label for the contract */
  strike_label: string;
  /** Trading exchange */
  exchange: string;
  /** Bid price of "yes" contract */
  bid_yes?: number;
  /** Bid price of "no" contract */
  bid_no?: number;
  /** Open interest for the contract */
  open_interest?: number;
  /** Candidate party (e.g., "Democrat", "Republican") */
  party?: string;
  /** Party affiliation, raw value from exchange */
  party_affiliation: string;
  /** Whether this candidate is the incumbent */
  incumbent?: boolean;
  /** Candidate status code from the exchange (e.g., "I", "C", "O") */
  candidate_status?: string;
  /** Matched contracts from different exchanges */
  matched?: ForecastTraderElectionGridMatchedContract[];
}

/**
 * Product (state-level race) in election grid response
 */
export interface ForecastTraderElectionGridProduct {
  /** Event product contract ID */
  conid: number;
  /** Product display name from API */
  name: string;
  /** State name (from level3 category) */
  state: string;
  /** Leading candidate "yes" contract identifier */
  leading_candidate_conid: number;
  /** Leading Democrat candidate "yes" contract identifier */
  leading_dem_conid?: number;
  /** Leading Republican candidate "yes" contract identifier */
  leading_rep_conid?: number;
  /** Leading party at the product level ('Democrat', 'Republican', etc.) — absent = toss-up */
  party?: string;
  /** Color intensity (0=toss-up, 1=lean, 2=likely, 3=solid) */
  color?: number;
  last_trade_time: number;
  /** Market-level open interest (sum across all candidates) */
  open_interest?: number;
  /** Total strikes for the race across all exchanges */
  strike_count?: number;
  /** Exchanges the race trades on (e.g., ["FORECASTX", "KALSHI"]) */
  exchanges?: string[];
  /**
   * Service-provided settlement period (e.g. "Nov'26"), as used by the
   * Prediction Hub's complex products. Real election races settle on a known
   * calendar date and don't set this; when present, the UI prefers it over
   * formatting `last_trade_time` as a date (FTAPI-231 balance-of-power rows).
   */
  measured_period?: string;
  /** List of contracts (candidate yes/no pairs) for this race */
  contracts: ForecastTraderElectionGridContract[];
}

/**
 * Response for GET /tws.proxy/forecasttrader/election/grid
 */
export interface ForecastTraderElectionGridResponse {
  /** List of election products (state races) */
  products: ForecastTraderElectionGridProduct[];
}

/**
 * Request params for POST /tws.proxy/forecasttrader/election/state/grid
 */
export interface ForecastTraderElectionStateGridParams {
  /** Comma-separated offices: "governor", "senate", "house" (query param) */
  offices?: string;
  /** Race type (query param) */
  raceType: ElectionRaceType;
  /** Required if raceType is primary (query param) */
  party?: PoliticalParty;
  /** Filters (request body) */
  filters?: {
    product?: {
      productConids?: number[];
    };
    contract?: {
      min_price?: number;
      max_price?: number;
      party?: ElectionPriceParty;
    };
  };
  sorting?: {
    product?: 'name' | 'open_interest';
  };
}

/**
 * State grouping in election state grid response
 */
export interface ForecastTraderElectionStateGridState {
  name: string;
  products: ForecastTraderElectionGridProduct[];
}

/**
 * Response for POST /tws.proxy/forecasttrader/election/state/grid
 */
export interface ForecastTraderElectionStateGridResponse {
  states: ForecastTraderElectionStateGridState[];
}
